import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import {
  ImportSourceArgsGroupInput,
  InstagramApiType,
  InstagramSearchSortType,
  InstagramSearchType,
  PlanImporterType,
  SocialProfileFields_InstagramSocialProfile_Fragment,
  ThirdPartyProfile
} from '../../../../models/graphql';
import { Select } from '../../../../components/forms/Select';
import { ContentFilterSubFilterWrapper } from '../ContentFilter';
import { SelectAutoComplete } from '../../../../components/forms/SelectAutoComplete';
import { FindContentContext, SearchStepEnum } from '../FindContentProvider';
import { SourceLevelAction } from '../SourceLevelAction';
import { SelectDropdownTemplate } from '../../../../components/forms/Select/Select.view';
import {
  autoCompleteProfileDropDownTemplate,
  autoCompleteProfileDropDownTemplateProps
} from '../SelectTemplate/SelectTemplate.view';
import { SocialNetworksEnum } from '../../../../constants/social-networks';
import classNames from 'classnames';
import classes from '../ContentFilter/ContentFilter.module.scss';
import { FormElementValueType } from '../../../../models/basic-objects';
import { useDidMount } from '../../../../hooks/useDidMount';
import { generateProfileList } from '../findContentHelpers';
import { SearchErrorBox } from '../SearchErrorBox';
import { Button } from '../../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import { Input } from '../../../../components/forms/Input';
import { useDimensions } from '../../../../hooks/useDimensions';
import { BreakpointsValuesEnum } from '../../../../constants/breakpoints-values';
import { AdvancedFeature } from 'components/containers/AdvancedFeature';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';
import { AdvancedFeatures } from 'components/containers/AdvancedFeature/AdvancedFeature.view';

export type InstagramFilterBarProps = {
  formInstance: UseFormReturn;
  filterSearchHandler: (resetFilterBar?: boolean) => void;
  isEmpty: boolean;
  width: number;
};

export type ContentFilterInstagramViewProps = {
  formInstance: UseFormReturn;
  autoCompleteLoading: boolean;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  selectedAutoCompleteValue: FormElementValueType;
  setSelectedAutoCompleteValue: (value: FormElementValueType) => void;
  instagramProfiles: SocialProfileFields_InstagramSocialProfile_Fragment[];
  submitFormHandler: (search: IGSearchTypesEnum | InstagramSearchType, profileId: string) => void;
  filterSearchHandler: (resetFilterBar?: boolean) => void;
  error: JSX.Element | string | undefined;
  hasPosts: boolean;
  thirdPartyProfiles: ThirdPartyProfile[];
  profileSearchHistory: ThirdPartyProfile[];
  showSaveSourceModal: boolean;
  setShowSaveSourceModal: (show: boolean) => void;
  getInstagramInputArgs: () => ImportSourceArgsGroupInput;
  apolloErrorKey: string | undefined;
  resetCallBackHandler: CallableFunction;
};

// Enum to define search types
export enum IGSearchTypesEnum {
  // HASHTAGS = 'HASHTAGS',
  BUSINESS_PROFILES = 'BUSINESS_PROFILES',
  BUSINESS_PROFILE_POSTS = 'BUSINESS_PROFILE_POSTS'
}

export const InstagramFilterBar = (props: InstagramFilterBarProps): JSX.Element => {
  const { formInstance, isEmpty, width } = props;
  const { loading, selectedProfileSupportedMetrics } = useContext(FindContentContext);
  const { importType, isTrial } = useOrganizationPlan();
  const isDisabledFeature = importType !== PlanImporterType.ADVANCED_IMPORTER;
  const filterBarContainerClasses = classNames('grid-x', classes.FilterBarContainer);

  const filterDefaultWrapperClasses = classNames('cell', classes.InputWrapper, {
    'small-12': width < BreakpointsValuesEnum.large
  });
  const filterInputWrapperClasses = classNames('cell', classes.InputWrapper, classes.HasButtonSibling, {
    'auto margin-top-small-8': width < BreakpointsValuesEnum.large,
    auto: width >= BreakpointsValuesEnum.large,
    [classes.InputWrapperMediumFilterWidth]: width >= BreakpointsValuesEnum.large
  });
  const filterButtonWrapperClasses = classNames('cell', classes.FilterButtonContainer, {
    'shrink margin-top-small-8': width < BreakpointsValuesEnum.large
  });

  // In place of setting default values, we determine if no value is set and do so
  // Setting default values will break loading a saved source
  useDidMount(() => {
    const { sortType } = formInstance.getValues();
    if (!sortType) {
      formInstance.setValue('sortType', InstagramSearchSortType.LATEST);
    }
  });

  return (
    <div className={filterBarContainerClasses}>
      <div className={filterDefaultWrapperClasses}>
        <Select
          items={[
            { value: InstagramSearchSortType.LATEST, label: 'Latest' },
            { value: InstagramSearchSortType.MOST_COMMENTED, label: 'Most Commented' },
            { value: InstagramSearchSortType.MOST_LIKED, label: 'Most Liked' }
          ]}
          name={'sortType'}
          size={'medium'}
          rules={{}}
          useFormInstance={formInstance}
          disabled={loading || isDisabledFeature || isEmpty || !Boolean(selectedProfileSupportedMetrics?.length)}
        />
      </div>
      <div className={filterInputWrapperClasses} style={{ maxWidth: '689px' }}>
        <Input
          formInstance={formInstance}
          name={'term'}
          placeholder={'Enter keyword or hashtag'}
          rules={{}}
          size={'medium'}
          disabled={loading || isDisabledFeature}
          onKeyDown={e => {
            // ENTER key presses submits form
            if (e.key === 'Enter') {
              props.filterSearchHandler();
            }
          }}
        />
      </div>
      <div className={filterButtonWrapperClasses}>
        <Button
          type={'button'}
          variant={'hollow'}
          size={'medium'}
          color={ButtonColorsEnum.primary}
          disabled={loading || isDisabledFeature}
          expanded={true}
          action={() => props.filterSearchHandler()}>
          Apply
        </Button>
      </div>
      {(isTrial || isDisabledFeature) && (
        <div className={classNames('cell', classes.AdvancedFeatureContainer)}>
          <AdvancedFeature feature={AdvancedFeatures.filterFeed} />
        </div>
      )}
    </div>
  );
};

export const ContentFilterInstagramView = (props: ContentFilterInstagramViewProps): JSX.Element => {
  const {
    formInstance,
    autoCompleteLoading,
    instagramProfiles,
    showFilters,
    selectedAutoCompleteValue,
    setSelectedAutoCompleteValue,
    submitFormHandler,
    thirdPartyProfiles,
    profileSearchHistory,
    filterSearchHandler,
    setShowSaveSourceModal,
    showSaveSourceModal,
    getInstagramInputArgs,
    apolloErrorKey,
    hasPosts,
    resetCallBackHandler
  } = props;
  const searchTypeValue = useWatch({ control: formInstance.control, name: 'type' });
  const businessProfileValue = useWatch({ control: formInstance.control, name: IGSearchTypesEnum.BUSINESS_PROFILES });
  const showHistoryLabel = !businessProfileValue && !!profileSearchHistory.length;

  const { loading, error, searchStep } = useContext(FindContentContext);

  const bodyRef = useRef<HTMLElement>(document.body);
  const { width } = useDimensions(bodyRef);

  const dropdownTemplate: SelectDropdownTemplate = useCallback(
    args => {
      const { expanded, items, setExpanded } = args;

      // If we show history, we need to merge the profiles in because thirdPartyProfiles does not have them all
      const profiles = [...thirdPartyProfiles, ...profileSearchHistory];

      const props: autoCompleteProfileDropDownTemplateProps = {
        expanded,
        showHistoryLabel,
        sourceType: SocialNetworksEnum.instagram,
        items,
        thirdPartyProfiles: profiles,
        setExpanded,
        selectedValue: selectedAutoCompleteValue,
        submitFormHandler: (value: string) => {
          const selectedItem = items.find(i => {
            return typeof i !== 'string' && i.value === value;
          });
          const label = selectedItem && typeof selectedItem !== 'string' ? selectedItem.label : selectedItem;

          formInstance.setValue(IGSearchTypesEnum.BUSINESS_PROFILES, label);

          setSelectedAutoCompleteValue(value);
          submitFormHandler(IGSearchTypesEnum.BUSINESS_PROFILE_POSTS, value);
        }
      };

      return autoCompleteProfileDropDownTemplate(props);
    },
    [
      showHistoryLabel,
      formInstance,
      profileSearchHistory,
      selectedAutoCompleteValue,
      setSelectedAutoCompleteValue,
      submitFormHandler,
      thirdPartyProfiles
    ]
  );

  const hasFacebookGraphApi = instagramProfiles.some(i => i.apiType === InstagramApiType.FACEBOOK_GRAPH);
  const hasInstagramGraphApi = instagramProfiles.some(i => i.apiType === InstagramApiType.INSTAGRAM_GRAPH);

  const thirdPartyProfileListItems = useMemo(
    () =>
      generateProfileList(
        thirdPartyProfiles,
        profileSearchHistory,
        selectedAutoCompleteValue,
        businessProfileValue,
        'username'
      ),
    [thirdPartyProfiles, selectedAutoCompleteValue, businessProfileValue, profileSearchHistory]
  );

  const IGProfileListItems = useMemo(() => {
    // Only allow graph types
    let profiles = instagramProfiles.filter(
      p => p.apiType === InstagramApiType.FACEBOOK_GRAPH || p.apiType === InstagramApiType.INSTAGRAM_GRAPH
    );

    return profiles.map(p => ({ label: p.displayName, value: p.id }));
  }, [instagramProfiles]);

  // Do not show search of profiles unless they have business/graph account
  const typeListItems = useMemo(() => {
    const search = hasFacebookGraphApi
      ? ['Search Instagram For', { label: 'Profiles', value: IGSearchTypesEnum.BUSINESS_PROFILES }]
      : [];
    const myPosts =
      hasFacebookGraphApi || hasInstagramGraphApi
        ? [{ label: 'Posts I’m tagged in', value: InstagramSearchType.MENTIONS }]
        : [];
    const own = [
      'Own Profiles',
      { label: 'My Feed', value: InstagramSearchType.PROFILE }, // default with profile
      ...myPosts
    ];

    return instagramProfiles.length > 0 ? [...search, ...own] : [{ label: 'No profile connected', value: -1 }];
  }, [hasFacebookGraphApi, hasInstagramGraphApi, instagramProfiles.length]);

  const defaultSearchWrapperClasses = classNames('cell', classes.InputWrapper, {
    'small-6': width < BreakpointsValuesEnum.large
  });

  const thirdSearchWrapperClasses = classNames('cell', classes.InputWrapper, {
    'small-12': width < BreakpointsValuesEnum.large,
    auto: width >= BreakpointsValuesEnum.large,
    [classes.InputWrapperDefaultMaxWidth]: width >= BreakpointsValuesEnum.large
  });

  useEffect(() => {
    if (instagramProfiles.length < 1) {
      formInstance.setValue('type', -1);
    }
    if (typeListItems.length > 1 && typeof typeListItems[1] === 'object') {
      formInstance.setValue('type', typeListItems[1].value);
      return;
    }
    formInstance.setValue('type', -1);
  }, [instagramProfiles, typeListItems, formInstance]);

  return (
    <>
      <div className={defaultSearchWrapperClasses}>
        <Select
          label={'Search Type'}
          items={typeListItems}
          name={'type'}
          size={'medium'}
          rules={{}}
          useFormInstance={formInstance}
          disabled={loading || instagramProfiles.length < 1}
        />
      </div>
      {searchTypeValue === IGSearchTypesEnum.BUSINESS_PROFILES && (
        <div className={thirdSearchWrapperClasses}>
          <SelectAutoComplete
            name={IGSearchTypesEnum.BUSINESS_PROFILES}
            label={'Type a Business Profile'}
            placeholder={'Enter profile name'}
            items={thirdPartyProfileListItems}
            formInstance={formInstance}
            size={'medium'}
            dropdownTemplate={dropdownTemplate}
            loading={autoCompleteLoading}
            disabled={loading}
            callBackHandler={(value: string) => {
              submitFormHandler(IGSearchTypesEnum.BUSINESS_PROFILES, value);
            }}
            resetCallBackHandler={resetCallBackHandler}
          />
        </div>
      )}
      {searchTypeValue === InstagramSearchType.PROFILE && (
        <div className={thirdSearchWrapperClasses}>
          <Select
            label={'Which Profile’s Feed?'}
            chooseLabel={'Choose a Profile'}
            hideDefaultSelectOption={true}
            items={IGProfileListItems}
            name={InstagramSearchType.PROFILE}
            size={'medium'}
            rules={{}}
            useFormInstance={formInstance}
            selectedValue={undefined}
            disabled={loading || IGProfileListItems.length === 0}
            action={(value, complete) => {
              complete(true);
              if (value && typeof value === 'string') {
                submitFormHandler(InstagramSearchType.PROFILE, value);
              }
            }}
          />
        </div>
      )}
      {searchTypeValue === InstagramSearchType.MENTIONS && (
        <div className={thirdSearchWrapperClasses}>
          <Select
            label={'Which Profile’s Tags?'}
            chooseLabel={'Choose a Profile'}
            hideDefaultSelectOption={true}
            items={IGProfileListItems}
            name={InstagramSearchType.MENTIONS}
            size={'medium'}
            rules={{}}
            useFormInstance={formInstance}
            selectedValue={undefined}
            disabled={loading || IGProfileListItems.length === 0}
            action={(value, complete) => {
              complete(true);
              if (value && typeof value === 'string') {
                submitFormHandler(InstagramSearchType.MENTIONS, value);
              }
            }}
          />
        </div>
      )}
      <ContentFilterSubFilterWrapper
        showFilters={
          showFilters && ((searchStep === SearchStepEnum.ONE && hasPosts) || searchStep > SearchStepEnum.ONE)
        }>
        <InstagramFilterBar
          formInstance={formInstance}
          filterSearchHandler={filterSearchHandler}
          isEmpty={!hasPosts}
          width={width}
        />
      </ContentFilterSubFilterWrapper>
      {/* Only show if we have posts from the 1 step */}
      {((showFilters && !error) || (searchStep === SearchStepEnum.TWO && !error)) && (
        <SourceLevelAction
          formInstance={formInstance}
          setShowSaveSourceModal={setShowSaveSourceModal}
          showSaveSourceModal={showSaveSourceModal}
          getArgsHandler={getInstagramInputArgs}
        />
      )}
      {error && !loading && (
        <div className={'cell'}>
          <SearchErrorBox apolloErrorKey={apolloErrorKey} filterSearchHandler={filterSearchHandler} />
        </div>
      )}
    </>
  );
};
